import { makePersisted } from "@solid-primitives/storage";
import {
  createContext,
  createSignal,
  ParentComponent,
  useContext,
} from "solid-js";
import { createStore } from "solid-js/store";

import {
  ApplicantSourceType,
  ContractTypeType,
  DrivingLicenceType,
  ScreeningGenderIdentity,
  ScreeningRightToWorkStatus,
  SponsorSwitchDesiredEmploymentType,
  StagedCandidateFilterCarAccess,
  StagedCandidateFilterDrivingLicence,
  StagedCandidateFilterPriority,
  StagedCandidateFilters,
  StagedCandidateStageType,
  WillingnessToRelocate,
  WorkAvailabilityV2,
} from "../../schema/graphql.js";

export interface IFilters extends StagedCandidateFilters {
  carAccess?: StagedCandidateFilterCarAccess;
  drivingLicence?: StagedCandidateFilterDrivingLicence;
  priority?: StagedCandidateFilterPriority;
  rightToWorkStatus?: ScreeningRightToWorkStatus | null;
  contractType?: ContractTypeType[];
  willingnessToRelocate?: WillingnessToRelocate | null;
  yearsOfExperience?: number;
  availabilityV2?: WorkAvailabilityV2[];
  stage?: StagedCandidateStageType | null;
  importedAfter?: Date | null;
  importedBefore?: Date | null;
  agencyId?: number | null;
  source?: ApplicantSourceType | null;
  searchQuery?: string;
  outcode?: string;
  hasVideos?: boolean | null;
  withinDistancePostcode?: string | null;
  withinDistanceKm?: number | null;
  currentCountry?: string | null;
  desiredEmploymentType?: SponsorSwitchDesiredEmploymentType[] | null;
  isDriver?: boolean | null;
  genderIdentity?: ScreeningGenderIdentity[] | null;
  drivingLicenceType: DrivingLicenceType | null;
}

export const CandidateFilterContext = createContext<{
  getFilters: () => IFilters;
  setFilters: (filters: IFilters) => void;
  getDefaultFilters: () => IFilters;
  setLoadedPresetId: (id: number | null) => void;
  getLoadedPresetId: () => number;
  filtersCurrentlyApplied: () => boolean;
  appliedTags: () => number[];
  setAppliedTags: (tagIds: number[]) => void;
}>();

const FILTER_KEY = "candidateFilters";
const FILTER_LOADED_PRESET_KEY = "candidateFiltersLoadedPresetId";
const STAGED_CANDIDATE_APPLIED_TAGS_KEY = "stagedCandidateAppliedTags";

const DEFAULT_FILTERS: IFilters = {
  carAccess: StagedCandidateFilterCarAccess.NoPreference,
  drivingLicence: StagedCandidateFilterDrivingLicence.NoPreference,
  priority: StagedCandidateFilterPriority.All,
  rightToWorkStatus: null,
  contractType: [],
  willingnessToRelocate: null,
  yearsOfExperience: 0,
  availabilityV2: [],
  stage: null,
  importedAfter: null,
  importedBefore: null,
  agencyId: null,
  source: null,
  searchQuery: "",
  outcode: "",
  hasVideos: null,
  withinDistancePostcode: null,
  withinDistanceKm: 50,
  currentCountry: null,
  desiredEmploymentType: null,
  isDriver: null,
  genderIdentity: null,
  drivingLicenceType: null,
};

const readStorage = () => {
  try {
    return Object.assign(
      {},
      DEFAULT_FILTERS,
      JSON.parse(window.sessionStorage.getItem(FILTER_KEY) || "{}"),
    );
  } catch {
    return DEFAULT_FILTERS;
  }
};

const getDefaultFilters = () => {
  return DEFAULT_FILTERS;
};

export const StagedCandidateFilterContextProvider: ParentComponent = (
  props,
) => {
  const [filtersStore, setFiltersStore] = createStore<IFilters>(readStorage());
  const [getLoadedPresetId, setLoadedPresetId] = makePersisted(
    createSignal<number>(0),
    {
      name: FILTER_LOADED_PRESET_KEY,
      storage: window.sessionStorage,
    },
  );

  const [appliedTags, setAppliedTags] = makePersisted(
    createSignal<number[]>([]),
    {
      name: STAGED_CANDIDATE_APPLIED_TAGS_KEY,
      storage: window.sessionStorage,
      serialize: (tags: number[]) => tags.join(","),
      deserialize: (tags: string) => tags.split(",").map(Number) ?? [],
    },
  );

  const setFilters = (filters: IFilters) => {
    window.sessionStorage.setItem(FILTER_KEY, JSON.stringify(filters));
    setFiltersStore(filters);
  };

  const getFilters = () => {
    const filters: IFilters = Object.assign({}, DEFAULT_FILTERS, filtersStore);
    return filters;
  };

  const filtersCurrentlyApplied = () => {
    return JSON.stringify(getFilters()) !== JSON.stringify(DEFAULT_FILTERS);
  };

  return (
    <CandidateFilterContext.Provider
      value={{
        setFilters,
        getFilters,
        getDefaultFilters,
        setLoadedPresetId,
        getLoadedPresetId,
        filtersCurrentlyApplied,
        appliedTags,
        setAppliedTags,
      }}
    >
      {props.children}
    </CandidateFilterContext.Provider>
  );
};
export function useCandidateFilters() {
  return useContext(CandidateFilterContext);
}
